import { useMutation, useQuery, useApolloClient, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'

import Card from '@10xLabs/react-web/components/Card'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import FormLayoutV2 from 'components/v2/FormLayout'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { EditService } from 'features/Services/components/EditService'
import { IEditServiceForm } from 'features/Services/components/EditService/types'
import { IItinerary } from 'features/Services/components/NewServicesV2/components/ItinerariesCard/types'
import { GET_SERVICE_V2 } from 'features/Services/queries-legacy'
import {
  ADD_ROUTE_SERVICE_PEAKS,
  CREATE_SERVICE_ITINERARIES_SCHEDULE,
  CREATE_VEHICLES_SCHEDULE,
  DELETE_VEHICLES_SCHEDULE,
  EDIT_ITINERARIES_SCHEDULE,
  EDIT_SERVICE,
  EDIT_VEHICLES_SCHEDULE,
  SCHEDULE_ROUTE_SERVICE_TRIPS
} from 'features/Services/mutations-legacy'
import {
  mapEditServiceToEditServiceInput,
  mapItinerariesToItinerariesSchedule,
  mapLimitsFormToLimitsInput,
  mapPeaksFormtoPeaksInput,
  mapServiceQueryToEditForm,
  mapVehiclesToVehiclesSchedule
} from 'models/services'
import { resolvers as routesResolvers } from 'resolvers/routes'
import { useLogin } from 'features/Login/hooks'
import { getDifference, getEntitiesDifference } from 'utils/object'
import { canWrite } from 'features/Users/utils'
import { IVehicleScheduleInput } from 'models/services/types'
import { ICapacityLimit } from 'features/Services/components/NewServicesV2/components/CapacityLimitsCard/types'
import { GTService, GTUpdateRouteServicePeakInput, GTUpdateServiceInput } from 'gql/types.generated'

const messages = defineMessages({
  helmet: {
    id: 'services.edit.helmet',
    defaultMessage: 'Edit service | Nexbus Admin'
  }
})

const EditServiceView = (): ReactElement => {
  const navigate = useNavigate()
  const { serviceId } = useParams()
  const intl = useIntl()
  const login = useLogin()
  const client = useApolloClient()
  client.addResolvers(routesResolvers)

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)

    if (canWrite(FEATURES.SERVICE, login.accessToken.policies)) {
      refetch()
    }
  }, [])

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery<{ service: GTService }>(GET_SERVICE_V2, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      serviceId
    }
  })
  const [addRouteServicePeaks, { loading: loadingAddRouteServicePeaks, error: errorAddRouteServicePeaks }] =
    useMutation(ADD_ROUTE_SERVICE_PEAKS)
  const [
    createServiceItinerariesSchedule,
    { loading: loadingCreateItinerarySchedule, error: errorCreateItinerarySchedule }
  ] = useMutation(CREATE_SERVICE_ITINERARIES_SCHEDULE)
  const [editItinerarySchedule, { loading: loadingEditItinerarySchedule, error: errorEditItinerarySchedule }] =
    useMutation(EDIT_ITINERARIES_SCHEDULE)
  const [createVehiclesSchedule, { loading: loadingCreateVehicleSchedule, error: errorCreateVehicleSchedule }] =
    useMutation(CREATE_VEHICLES_SCHEDULE)
  const [deleteVehiclesSchedule, { loading: loadingDeleteVehicleSchedule, error: errorDeleteVehicleSchedule }] =
    useMutation(DELETE_VEHICLES_SCHEDULE)
  const [editVehiclesSchedule, { loading: loadingEditVehicleSchedule, error: errorEditVehicleSchedule }] =
    useMutation(EDIT_VEHICLES_SCHEDULE)
  const [editService, { loading: loadingEditService, error: errorEditService }] = useMutation(EDIT_SERVICE)
  const [scheduleServiceTrips, { loading: loadingScheduleServiceTrips, error: errorScheduleServiceTrips }] =
    useMutation(SCHEDULE_ROUTE_SERVICE_TRIPS)

  const loading =
    loadingEditItinerarySchedule ||
    loadingEditVehicleSchedule ||
    loadingEditService ||
    loadingCreateVehicleSchedule ||
    loadingCreateItinerarySchedule ||
    loadingDeleteVehicleSchedule ||
    loadingScheduleServiceTrips ||
    loadingAddRouteServicePeaks
  const error =
    errorEditItinerarySchedule ||
    errorEditVehicleSchedule ||
    errorEditService ||
    errorCreateVehicleSchedule ||
    errorCreateItinerarySchedule ||
    errorDeleteVehicleSchedule ||
    errorScheduleServiceTrips ||
    errorAddRouteServicePeaks

  if (!canWrite(FEATURES.SERVICE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.SERVICE} />
  }

  let content = <></>
  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="services.edit.error.description"
              defaultMessage="There was an error while loading your service"
            />
          </EmptyState.Description>
          <EmptyState.Action>
            <Button
              onClick={(): void => {
                refetch()
              }}
              kind="primary"
            >
              <FormattedMessage id="services.edit.error.action" defaultMessage="Reload your service" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    const oldService = mapServiceQueryToEditForm(queryData?.service as GTService)

    const handleSubmit = async (newService: IEditServiceForm): Promise<void> => {
      const {
        createdEntities: createdItineraries,
        editedEntities: editedItineraries,
        hasDifferences: hasItinerariesDifferences
      } = getEntitiesDifference<IItinerary>(newService.services[0].itineraries, oldService.services[0].itineraries)
      if (hasItinerariesDifferences) {
        const editedItinerariesSchedule = mapItinerariesToItinerariesSchedule(editedItineraries)
        const createdItinerariesSchedule = mapItinerariesToItinerariesSchedule(createdItineraries)
        if (!isEmpty(editedItinerariesSchedule)) {
          await editItinerarySchedule({
            variables: {
              input: {
                id: newService.id,
                itinerariesSchedule: editedItinerariesSchedule
              }
            }
          })
        }
        if (!isEmpty(createdItinerariesSchedule)) {
          await createServiceItinerariesSchedule({
            variables: {
              input: {
                id: newService.id,
                itinerariesSchedule: createdItinerariesSchedule
              }
            }
          })
        }
      }

      const newVehiclesSchedule = mapVehiclesToVehiclesSchedule(newService.services[0].vehicles)
      const oldVehiclesSchedule = mapVehiclesToVehiclesSchedule(oldService.services[0].vehicles)
      const {
        createdEntities: createdVehiclesSchedule,
        editedEntities: editedVehiclesSchedule,
        deletedEntities: deletedVehiclesSchedule,
        hasDifferences: hasVehiclesDifferences
      } = getEntitiesDifference<IVehicleScheduleInput>(newVehiclesSchedule, oldVehiclesSchedule)
      if (hasVehiclesDifferences) {
        if (!isEmpty(deletedVehiclesSchedule)) {
          await deleteVehiclesSchedule({
            variables: {
              input: {
                id: newService.id,
                vehiclesScheduleIDs: deletedVehiclesSchedule.map(({ id }) => id)
              }
            }
          })
        }
        if (!isEmpty(editedVehiclesSchedule)) {
          await editVehiclesSchedule({
            variables: {
              input: {
                id: newService.id,
                vehiclesSchedule: editedVehiclesSchedule
              }
            }
          })
        }
        if (!isEmpty(createdVehiclesSchedule)) {
          await createVehiclesSchedule({
            variables: {
              input: {
                id: newService.id,
                vehiclesSchedule: createdVehiclesSchedule
              }
            }
          })
        }
      }

      const oldSettings = mapEditServiceToEditServiceInput(oldService)
      const newSettings = mapEditServiceToEditServiceInput(newService)
      const settingsPayload = getDifference(newSettings, oldSettings)
      const oldPeakSettings = mapPeaksFormtoPeaksInput(oldService.peakSettings)
      const newPeakSettings = mapPeaksFormtoPeaksInput(newService.peakSettings)
      const peakSettingsPayload = getEntitiesDifference(newPeakSettings, oldPeakSettings)

      if (!isEmpty(settingsPayload) || !isEmpty(peakSettingsPayload.editedEntities)) {
        let editServiceInput: GTUpdateServiceInput = {
          id: newService.id
        }
        if (!isEmpty(settingsPayload)) {
          editServiceInput = {
            ...editServiceInput,
            ...settingsPayload
          }
          if (settingsPayload.limits) {
            editServiceInput.limits = newSettings.limits.map((limit: ICapacityLimit) =>
              mapLimitsFormToLimitsInput(limit, newService.isPetFriendly)
            )
          }
        }
        if (!isEmpty(peakSettingsPayload.editedEntities)) {
          editServiceInput = {
            ...editServiceInput,
            peaks: peakSettingsPayload.editedEntities as GTUpdateRouteServicePeakInput[]
          }
        }
        await editService({
          variables: {
            service: editServiceInput
          }
        })
      }

      if (!isEmpty(peakSettingsPayload.createdEntities)) {
        await addRouteServicePeaks({
          variables: {
            input: {
              id: newService.id,
              peaks: peakSettingsPayload.createdEntities
            }
          }
        })
      }
      await scheduleServiceTrips({
        variables: {
          input: {
            id: newService.id
          }
        }
      })
      navigate('/routes')
    }

    content = (
      <EditService
        service={oldService}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        canEdit={canWrite(FEATURES.SERVICE, login.accessToken.policies)}
      />
    )
  }

  return <FormLayoutV2.Container>{content}</FormLayoutV2.Container>
}

export default withLastUrl(EditServiceView)
